import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inventoryAPI, warehouseAPI, finishedGoodAPI, rawMaterialAPI } from '../utils/api';
import { Boxes, History, ClipboardCheck, Edit2, Search, Plus, ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import Modal from '../components/common/Modal';
import { localName, fmt, apiError, today } from '../utils/helpers';
import SectionHero from '../components/common/SectionHero';

const MOVE_BADGE = {
  opening: 'badge-blue', production_in: 'badge-green', production_out: 'badge-orange', production_cancel: 'badge-red',
  sale: 'badge-orange', sale_cancel: 'badge-red', lading: 'badge-orange', lading_cancel: 'badge-red',
  transfer_in: 'badge-blue', transfer_out: 'badge-blue', adjust: 'badge-yellow', count: 'badge-yellow',
  purchase: 'badge-green', purchase_cancel: 'badge-red',
};
const MOVE_TYPES = Object.keys(MOVE_BADGE);

function TypeToggle({ value, onChange, t }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {[['product', 'finished_goods'], ['raw', 'raw_materials']].map(([k, l]) => (
        <button key={k} className="btn btn-sm" onClick={() => onChange(k)}
          style={{ background: value === k ? 'var(--accent)' : 'rgba(255,255,255,0.04)', color: value === k ? '#fff' : 'var(--text-2)', border: '1px solid var(--border)' }}>{t(l)}</button>
      ))}
    </div>
  );
}

export default function Inventory() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { user } = useAuthStore();
  const canEdit = ['admin', 'manager', 'production_manager'].includes(user?.role);
  const [tab, setTab] = useState('stock');

  return (
    <div className="space-y-5">
      <SectionHero title={t('inventory')} />
      <div className="tabs">
        <button className={`tab ${tab === 'stock' ? 'active' : ''}`} onClick={() => setTab('stock')}><Boxes size={14} /> {t('stock')}</button>
        <button className={`tab ${tab === 'kardex' ? 'active' : ''}`} onClick={() => setTab('kardex')}><History size={14} /> {t('kardex')}</button>
        <button className={`tab ${tab === 'counts' ? 'active' : ''}`} onClick={() => setTab('counts')}><ClipboardCheck size={14} /> {t('stock_count')}</button>
      </div>
      {tab === 'stock' && <StockTab t={t} lang={lang} canEdit={canEdit} />}
      {tab === 'kardex' && <KardexTab t={t} lang={lang} />}
      {tab === 'counts' && <CountsTab t={t} lang={lang} canEdit={canEdit} />}
    </div>
  );
}

// ======================================================================= stock matrix + direct edit
function StockTab({ t, lang, canEdit }) {
  const [type, setType] = useState('product');
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ quantity: '', sqm: '', reason: '' });
  const [saving, setSaving] = useState(false);

  const load = () => inventoryAPI.stock(type).then(r => setData(r.data)).catch(() => toast.error(t('error')));
  useEffect(() => { setData(null); load(); }, [type]); // eslint-disable-line

  const q = search.trim().toLowerCase();
  const items = (data?.items || []).filter(i => !q || [i.name, i.name_en, i.name_ru].some(v => (v || '').toLowerCase().includes(q)));
  const whName = id => data?.warehouses.find(w => w.id === id)?.name;

  const open = (item, cell) => {
    if (!canEdit) return;
    setEdit({ item, cell });
    setForm({ quantity: cell.quantity, sqm: cell.sqm ?? '', reason: '' });
  };
  const save = async () => {
    const qty = parseFloat(form.quantity);
    if (isNaN(qty) || qty < 0) return toast.error(t('invalid_quantity'));
    if (!form.reason.trim()) return toast.error(t('reason_required'));
    setSaving(true);
    try {
      await inventoryAPI.setStock({ item_type: type, item_id: edit.item.id, warehouse_id: edit.cell.warehouse_id, quantity: qty,
        sqm: type === 'product' && form.sqm !== '' ? parseFloat(form.sqm) : null, reason: form.reason });
      toast.success(t('saved')); setEdit(null); load();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <TypeToggle value={type} onChange={setType} t={t} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 200px' }}>
          <Search size={15} style={{ color: 'var(--text-muted)' }} />
          <input className="input" style={{ border: 'none', background: 'transparent', padding: 0 }} placeholder={t('search') + '...'} value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {canEdit && <span className="text-xs" style={{ color: 'var(--text-muted)' }}><Edit2 size={11} /> {t('click_cell_to_edit')}</span>}
      </div>
      {!data ? <div className="loading-center"><div className="spinner" /></div> : items.length === 0 ? <div className="empty-state"><p>{t('no_data')}</p></div> : (
        <div className="table-wrap"><table>
          <thead><tr>
            <th>{type === 'raw' ? t('raw_material') : t('product')}</th>
            {data.warehouses.map(w => <th key={w.id}>{w.name}</th>)}
            <th>{t('total')}</th>
          </tr></thead>
          <tbody>{items.map(it => (
            <tr key={it.id}>
              <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{localName(it, lang)} <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{type === 'product' ? t('rolls') : it.unit}</span></td>
              {it.stocks.map(c => (
                <td key={c.warehouse_id} onClick={() => open(it, c)} style={{ cursor: canEdit ? 'pointer' : 'default' }} title={canEdit ? t('edit') : ''}>
                  <span style={{ padding: '4px 8px', borderRadius: 8, border: canEdit ? '1px dashed rgba(255,255,255,0.12)' : 'none', display: 'inline-block', minWidth: 54,
                    color: c.quantity < 0 ? '#f87171' : c.quantity === 0 ? 'var(--text-muted)' : 'var(--text-primary)' }}>
                    {fmt(c.quantity)}{type === 'product' && <span className="text-xs" style={{ color: 'var(--text-muted)' }}> / {fmt(c.sqm)} {t('sqm')}</span>}
                  </span>
                </td>
              ))}
              <td style={{ fontWeight: 700, color: it.total <= (it.threshold || 0) ? '#fbbf24' : 'var(--text-primary)' }}>{fmt(it.total)}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}

      {edit && (
        <Modal title={t('edit_stock')} onClose={() => setEdit(null)} style={{ maxWidth: 440 }}
          footer={<><button className="btn btn-secondary" onClick={() => setEdit(null)}>{t('cancel')}</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? t('loading') : t('save')}</button></>}>
          <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 12, background: 'rgba(0,0,0,0.25)', border: '1px solid var(--border)' }}>
            <div style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{localName(edit.item, lang)}</div>
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{whName(edit.cell.warehouse_id)} · {t('current_stock')}: {fmt(edit.cell.quantity)}{type === 'product' ? ` ${t('rolls')} / ${fmt(edit.cell.sqm)} ${t('sqm')}` : ` ${edit.item.unit}`}</div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{type === 'product' ? t('rolls') : t('quantity')} *</label>
              <input className="input" type="number" min="0" step="any" autoFocus value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} /></div>
            {type === 'product' && <div className="form-group"><label className="form-label">{t('sqm')}</label>
              <input className="input" type="number" min="0" step="any" value={form.sqm} onChange={e => setForm(f => ({ ...f, sqm: e.target.value }))} /></div>}
          </div>
          {form.quantity !== '' && !isNaN(parseFloat(form.quantity)) && (
            <p className="text-xs mb-3" style={{ color: parseFloat(form.quantity) >= edit.cell.quantity ? 'var(--green)' : 'var(--red)' }}>
              {t('difference')}: {parseFloat(form.quantity) - edit.cell.quantity > 0 ? '+' : ''}{fmt(parseFloat(form.quantity) - edit.cell.quantity)}
            </p>
          )}
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">{t('reason')} *</label>
            <input className="input" value={form.reason} placeholder={t('reason_placeholder')} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} /></div>
        </Modal>
      )}
    </div>
  );
}

// ======================================================================= kardex
function KardexTab({ t, lang }) {
  const [warehouses, setWarehouses] = useState([]);
  const [goods, setGoods] = useState([]);
  const [mats, setMats] = useState([]);
  const [f, setF] = useState({ item_type: '', item_id: '', warehouse_id: '', move_type: '', date_from: '', date_to: '' });
  const [rows, setRows] = useState(null);

  useEffect(() => {
    Promise.all([warehouseAPI.list(), finishedGoodAPI.list(), rawMaterialAPI.list()])
      .then(([w, g, m]) => { setWarehouses(w.data); setGoods(g.data); setMats(m.data); }).catch(() => {});
  }, []);
  useEffect(() => {
    const p = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    setRows(null);
    inventoryAPI.movements(p).then(r => setRows(r.data)).catch(() => toast.error(t('error')));
  }, [f]); // eslint-disable-line

  const itemOptions = f.item_type === 'raw' ? mats : f.item_type === 'product' ? goods : [];
  const set = (k, v) => setF(x => ({ ...x, [k]: v, ...(k === 'item_type' ? { item_id: '' } : {}) }));

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
        <select className="select" value={f.item_type} onChange={e => set('item_type', e.target.value)}>
          <option value="">{t('all_items')}</option><option value="product">{t('finished_goods')}</option><option value="raw">{t('raw_materials')}</option>
        </select>
        <select className="select" value={f.item_id} disabled={!f.item_type} onChange={e => set('item_id', e.target.value)}>
          <option value="">{t('all')}</option>{itemOptions.map(i => <option key={i.id} value={i.id}>{localName(i, lang)}</option>)}
        </select>
        <select className="select" value={f.warehouse_id} onChange={e => set('warehouse_id', e.target.value)}>
          <option value="">{t('all_warehouses')}</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <select className="select" value={f.move_type} onChange={e => set('move_type', e.target.value)}>
          <option value="">{t('all_move_types')}</option>{MOVE_TYPES.map(m => <option key={m} value={m}>{t('move_' + m)}</option>)}
        </select>
        <input className="input" type="date" value={f.date_from} onChange={e => set('date_from', e.target.value)} />
        <input className="input" type="date" value={f.date_to} onChange={e => set('date_to', e.target.value)} />
      </div>
      {!rows ? <div className="loading-center"><div className="spinner" /></div> : rows.length === 0 ? <div className="empty-state"><p>{t('no_data')}</p></div> : (
        <div className="table-wrap"><table>
          <thead><tr><th>{t('date')}</th><th>{t('item')}</th><th>{t('warehouse')}</th><th>{t('move_type')}</th><th>{t('change')}</th><th>{t('balance')}</th><th className="hide-mobile">{t('reference')}</th><th className="hide-mobile">{t('user')}</th></tr></thead>
          <tbody>{rows.map(r => {
            const up = r.qty_change >= 0;
            return (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{r.move_date}<div className="text-xs" style={{ color: 'var(--text-muted)' }}>{String(r.created_at).slice(11, 16)}</div></td>
                <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{localName(r, lang)}</td>
                <td>{r.warehouse}</td>
                <td><span className={`badge ${MOVE_BADGE[r.move_type] || ''}`}>{t('move_' + r.move_type, r.move_type)}</span></td>
                <td style={{ color: up ? '#34d399' : '#f87171', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />} {up ? '+' : ''}{fmt(r.qty_change)} {r.item_type === 'raw' ? r.unit : t('rolls')}
                  {r.item_type === 'product' && r.sqm_change ? <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{r.sqm_change > 0 ? '+' : ''}{fmt(r.sqm_change)} {t('sqm')}</div> : null}
                </td>
                <td style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(r.balance_after)}</td>
                <td className="hide-mobile">{r.ref_type === 'count' ? t('move_count') : r.ref_type === 'manual' ? t('move_adjust') : (r.ref_label || '—')}{r.note && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{r.note}</div>}</td>
                <td className="hide-mobile">{r.user || '—'}</td>
              </tr>
            );
          })}</tbody>
        </table></div>
      )}
    </div>
  );
}

// ======================================================================= stock count (انبارگردانی)
function CountsTab({ t, lang, canEdit }) {
  const [counts, setCounts] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [modal, setModal] = useState(false);
  const [setup, setSetup] = useState({ warehouse_id: '', item_type: 'product', count_date: today(), notes: '' });
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => inventoryAPI.counts().then(r => setCounts(r.data)).catch(() => toast.error(t('error')));
  useEffect(() => { load(); warehouseAPI.list().then(r => setWarehouses(r.data)).catch(() => {}); }, []); // eslint-disable-line

  const loadLines = async (s) => {
    if (!s.warehouse_id) { setLines([]); return; }
    const r = await inventoryAPI.stock(s.item_type);
    setLines(r.data.items.map(it => {
      const c = it.stocks.find(x => x.warehouse_id === s.warehouse_id) || { quantity: 0, sqm: 0 };
      return { item_id: it.id, item: it, system_qty: c.quantity, system_sqm: c.sqm ?? null, counted_qty: String(c.quantity), counted_sqm: c.sqm != null ? String(c.sqm) : '' };
    }));
  };
  const setS = (k, v) => { const s = { ...setup, [k]: v }; setSetup(s); if (k !== 'notes' && k !== 'count_date') loadLines(s); };
  const diffs = useMemo(() => lines.filter(l => parseFloat(l.counted_qty) !== l.system_qty ||
    (setup.item_type === 'product' && l.counted_sqm !== '' && parseFloat(l.counted_sqm) !== l.system_sqm)).length, [lines, setup.item_type]);

  const submit = async () => {
    if (!setup.warehouse_id) return toast.error(t('select_warehouse'));
    if (lines.some(l => l.counted_qty === '' || isNaN(parseFloat(l.counted_qty)) || parseFloat(l.counted_qty) < 0)) return toast.error(t('invalid_quantity'));
    if (!window.confirm(t('confirm_count').replace('{n}', diffs))) return;
    setSaving(true);
    try {
      const r = await inventoryAPI.createCount({ ...setup, lines: lines.map(l => ({ item_id: l.item_id, counted_qty: parseFloat(l.counted_qty),
        counted_sqm: setup.item_type === 'product' && l.counted_sqm !== '' ? parseFloat(l.counted_sqm) : null })) });
      toast.success(`${t('saved')} · ${r.data.adjusted} ${t('adjusted_items')}`);
      setModal(false); setLines([]); load();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  return (
    <>
      {canEdit && <div className="flex justify-end"><button className="btn btn-primary" onClick={() => { setSetup({ warehouse_id: '', item_type: 'product', count_date: today(), notes: '' }); setLines([]); setModal(true); }}><Plus size={16} /> {t('new_stock_count')}</button></div>}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {!counts ? <div className="loading-center"><div className="spinner" /></div> : counts.length === 0 ? <div className="empty-state"><ClipboardCheck size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{t('no_data')}</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th style={{ width: 32 }}></th><th>{t('date')}</th><th>{t('warehouse')}</th><th>{t('type')}</th><th>{t('items')}</th><th>{t('differences')}</th><th className="hide-mobile">{t('user')}</th></tr></thead>
            <tbody>{counts.map(c => (
              <React.Fragment key={c.id}>
                <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(e => ({ ...e, [c.id]: !e[c.id] }))}>
                  <td>{expanded[c.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</td>
                  <td>{c.count_date}</td><td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{c.warehouse}</td>
                  <td>{c.item_type === 'raw' ? t('raw_materials') : t('finished_goods')}</td>
                  <td>{c.lines_count}</td>
                  <td>{c.diff_count > 0 ? <span className="badge badge-yellow">{c.diff_count}</span> : <span className="badge badge-green">0</span>}</td>
                  <td className="hide-mobile">{c.user}</td>
                </tr>
                {expanded[c.id] && (
                  <tr><td colSpan={7} style={{ background: 'rgba(0,0,0,0.2)', padding: '10px 20px' }}>
                    {c.notes && <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{c.notes}</p>}
                    <table><thead><tr><th>{t('item')}</th><th>{t('system_qty')}</th><th>{t('counted_qty')}</th><th>{t('difference')}</th></tr></thead>
                      <tbody>{c.lines.map((l, i) => { const d = l.counted_qty - l.system_qty; return (
                        <tr key={i}><td>{l.name}</td><td>{fmt(l.system_qty)}</td><td>{fmt(l.counted_qty)}</td>
                          <td style={{ color: d === 0 ? 'var(--text-muted)' : d > 0 ? '#34d399' : '#f87171', fontWeight: 700 }}>{d > 0 ? '+' : ''}{fmt(d)}</td></tr>); })}</tbody></table>
                  </td></tr>
                )}
              </React.Fragment>
            ))}</tbody>
          </table></div>
        )}
      </div>

      {modal && (
        <Modal title={t('new_stock_count')} size="modal-lg" onClose={() => setModal(false)}
          footer={<><span className="text-xs" style={{ color: diffs ? '#fbbf24' : 'var(--text-muted)', marginInlineEnd: 'auto' }}>{t('differences')}: {diffs}</span>
            <button className="btn btn-secondary" onClick={() => setModal(false)}>{t('cancel')}</button>
            <button className="btn btn-primary" onClick={submit} disabled={saving || !lines.length}>{saving ? t('loading') : t('apply_count')}</button></>}>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('warehouse')} *</label>
              <select className="select" value={setup.warehouse_id} onChange={e => setS('warehouse_id', e.target.value)}>
                <option value="">{t('select_warehouse')}</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">{t('type')}</label>
              <select className="select" value={setup.item_type} onChange={e => setS('item_type', e.target.value)}>
                <option value="product">{t('finished_goods')}</option><option value="raw">{t('raw_materials')}</option>
              </select></div>
            <div className="form-group"><label className="form-label">{t('date')}</label><input className="input" type="date" value={setup.count_date} onChange={e => setS('count_date', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">{t('notes')}</label><input className="input" value={setup.notes} onChange={e => setS('notes', e.target.value)} /></div>
          {lines.length > 0 && (
            <div className="table-wrap"><table>
              <thead><tr><th>{t('item')}</th><th>{t('system_qty')}</th><th>{t('counted_qty')}</th>{setup.item_type === 'product' && <th>{t('counted_sqm')}</th>}<th>{t('difference')}</th></tr></thead>
              <tbody>{lines.map((l, i) => {
                const d = (parseFloat(l.counted_qty) || 0) - l.system_qty;
                const upd = (k, v) => setLines(ls => ls.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
                return (
                  <tr key={l.item_id}>
                    <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{localName(l.item, lang)}</td>
                    <td>{fmt(l.system_qty)}{setup.item_type === 'product' && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{fmt(l.system_sqm)} {t('sqm')}</div>}</td>
                    <td><input className="input" style={{ width: 110, padding: '6px 10px' }} type="number" min="0" step="any" value={l.counted_qty} onChange={e => upd('counted_qty', e.target.value)} /></td>
                    {setup.item_type === 'product' && <td><input className="input" style={{ width: 110, padding: '6px 10px' }} type="number" min="0" step="any" value={l.counted_sqm} onChange={e => upd('counted_sqm', e.target.value)} /></td>}
                    <td style={{ color: d === 0 ? 'var(--text-muted)' : d > 0 ? '#34d399' : '#f87171', fontWeight: 700 }}>{d > 0 ? '+' : ''}{fmt(d)}</td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          )}
        </Modal>
      )}
    </>
  );
}
